import React from 'react';
import styled from '@emotion/styled';
import type { Nullable, SignupFlow, StringMap } from 'corgi-types';

import { CaptionRegularCR1 } from 'common/components/Typography';
import { useLoginFormUrls } from 'common/hooks/form/login';
import { useLocalization } from 'common/providers/LocaleProvider';
import AppleButton from './AppleSignInButton';
import GoogleButton from './GoogleButton';
import { useThirdPartyLoginHandler } from './hooks/third-party-login';
import Separator from './Separator';

interface ThirdPartyLoginSectionProps {
  userFlow: SignupFlow;
  setCommonError: (errorMessage: Nullable<string>) => void;
  onSuccess: (args?: { queryParams: StringMap }) => void;
  appleStateToken: string;
  appleClientId: string;
  withAppleFirst: boolean;
  withDisclaimer?: boolean;
  withSeparator?: boolean;
  className?: string;
  referredBy?: string;
  withDisabledButtons?: boolean;
}

const Container = styled.div``;

// eslint-disable-next-line complexity
const ThirdPartyLoginSection = ({
  userFlow,
  className,
  onSuccess,
  appleClientId,
  appleStateToken,
  setCommonError: setError,
  withSeparator = true,
  withDisclaimer = true,
  withAppleFirst = false,
  withDisabledButtons = false,
  referredBy,
}: ThirdPartyLoginSectionProps) => {
  const { t, r } = useLocalization();
  const { performThirdPartyLogin, passwordModal } = useThirdPartyLoginHandler({
    userFlow,
    onSuccess,
    setCommonError: setError,
    appleClientId,
    appleStateToken,
    referredBy,
  });
  const { forgotPasswordUrl } = useLoginFormUrls(userFlow);

  const setCommonError = () => {
    setError(
      r.t('h24_third_party_login_error', {
        forgotPasswordUrl,
      })
    );
  };

  const googleButton = (
    <GoogleButton
      setCommonError={setCommonError}
      performLogin={performThirdPartyLogin}
      {...(!withSeparator ? { mt: 'sp_0' } : {})}
      {...(!withDisclaimer ? { mb: 'sp_0' } : {})}
      {...(!!appleClientId && !!appleStateToken ? { mb: 'sp_12' } : {})}
      {...(withAppleFirst ? { mt: 'sp_12', mb: '0' } : {})}
      isDisabled={withDisabledButtons}
    />
  );

  return (
    <Container className={className}>
      {withSeparator && <Separator />}

      {!withAppleFirst && googleButton}

      {!!appleClientId && !!appleStateToken ? (
        <AppleButton
          clientId={appleClientId}
          stateToken={appleStateToken}
          setCommonError={setCommonError}
          performLogin={performThirdPartyLogin}
          isDisabled={withDisabledButtons}
        />
      ) : withDisclaimer ? (
        <CaptionRegularCR1>{t('h24_apple_login_disclaimer')}</CaptionRegularCR1>
      ) : null}

      {withAppleFirst && googleButton}

      {passwordModal}
    </Container>
  );
};

export default ThirdPartyLoginSection;
